
import { Logger, hashEmail, getPageName } from "../../helpers";

/**
 * @fileoverview Manages event data, preparing payloads and handling user data.
 * @module DataManager
 */

/**
 * The `DataManager` class handles the preparation and management of event data,
 * including user data such as hashed emails, tracking parameters, and payloads for events.
 *
 * ### High-Level Logic:
 * - Prepares event payloads with common data.
 * - Handles hashing of emails, extraction of tracking parameters (fbq, fbc), and user identification.
 *
 * ### Structure:
 * - The class interacts with session storage to store and retrieve user data.
 * - Provides methods to prepare payloads for different types of events.
 *
 * @example
 * const dataManager = new DataManager();
 * dataManager.initialize();
 * const payload = dataManager.prepareCommonPayload(EVENTS.PageView, {}, "event_id_123");
 */
class DataManager {

    // Session storage key for user data
    userDataSessionKey = "userData";
    // Base user data structure with default values
    defaultUserData = {
        external_id: "anonymous", // Default external ID
        fbq: null, // Facebook pixel tracking ID
        fbc: null, // Facebook click ID
        rdt_cid: null, // Reddit click ID
        hashedEmail: null, // Hashed email for user identification
    };

    constructor() {
    }

    /**
     * Initializes the DataManager by ensuring user data is present in session storage.
     * @method initialize
     * @memberof DataManager
     */ 
    initialize( hasOptedOut = false ) {
        // If the user data is not in session storage, initialize it
        Logger.log("Initializing DataManager and checking user data in session storage.");
        if ( !hasOptedOut ) {
            // Check for the Meta fbq and fbc tracking parameters
            const fbq = this.getFbq();
            const fbc = this.getFbc();
            const rdt_cid = this.getRdtCid();
            // Store the fbq and fbc to the user data if they are found
            this.updateUserData({ 
                fbq,
                fbc,
                rdt_cid,
            });
        } else {
            Logger.log("User has opted out, clearing userData.");
            // If the user has opted out, reset the user data to default
            this.deleteUserData();
        }
    }

    /**
     * Updates user data, hashing the email if provided, and stores it in session storage.
     * @method updateUserData
     * @memberof DataManager
     * @param {Object} newData - The new user data to update.
     * @returns {void}
     */
    updateUserData( newData ) {
        Logger.log("Updating user data in session storage with new data:", newData);
        // Ensure newData is an object

        // Get current user data from session storage
        const userData = this.getUserData();

        // If a new email is provided, hash it and update the user data
        if ( newData.hashedEmail ) { userData.hashedEmail = newData.hashedEmail; }
        // If a new external_id is provided, update it
        if ( newData.external_id ) { userData.external_id = newData.external_id; }
        // If a new fbq is provided, update it
        if ( newData.fbq ) { userData.fbq = newData.fbq; }
        // If a new fbc is provided, update it
        if ( newData.fbc ) { userData.fbc = newData.fbc; }
        // If a new rdt_cid is provided, update it
        if ( newData.rdt_cid ) { userData.rdt_cid = newData.rdt_cid; }

        // Store the userData in session storage
        sessionStorage.setItem(
            this.userDataSessionKey,
            JSON.stringify(userData)
        );
    }

    /**
     * Retrieves user data from session storage or initializes it.
     * @async
     * @method getUserData
     * @memberof DataManager
     * @returns {Object} The user data object.
     */
    getUserData() {
        Logger.log("Retrieving user data from session storage.");

        // If the session storage does not have user data, initialize it
        if ( !sessionStorage.getItem(this.userDataSessionKey) ) {
            Logger.log("User data not found in session storage, initializing.");
            // Initialize with default user data
            sessionStorage.setItem(
                this.userDataSessionKey,
                JSON.stringify(this.defaultUserData)
            );
        }

        const userData = sessionStorage.getItem(this.userDataSessionKey);
        // Return the user data from session storage
        return JSON.parse( userData );
    }

    /**
     * Deletes the user data from session storage.
     * @method deleteUserData
     * @memberof DataManager
     * @returns {void}
     */
    deleteUserData() {
        Logger.log("Resetting user data from session storage.");
        // Optionally, reset the user data to default
        sessionStorage.setItem(
            this.userDataSessionKey,
            JSON.stringify(this.defaultUserData)
        );
    }
    
    /**
     * Extracts the fbq tracking parameter from cookies or the document referrer.
     * @method getFbq
     * @memberof DataManager
     * @returns {string|null} The fbq value or null if not found.
     */
    getFbq() {
        const fbqCookie = this.getCookie("_fbp");
        Logger.log("Retrieved fbq:", fbqCookie);
        if ( fbqCookie ) return fbqCookie;

        const referrer = document.referrer;
        if ( referrer.includes("facebook.com") ) {
            const extractedFbq = this.extractFbqFromReferrer(referrer);
            Logger.log("Extracted fbq from referrer:", extractedFbq);
            return extractedFbq;
        }

        return null;
    }

    /**
     * Helper to extract fbq from referrer string.
     * @method extractFbqFromReferrer
     * @memberof DataManager
     * @param {string} referrer
     * @returns {string|null}
     */
    extractFbqFromReferrer(referrer) {
        try {
            const url = new URL(referrer);
            return url.searchParams.get("fbq") || null;
        } catch (error) {
            Logger.error("Error extracting fbq from referrer:", error);
            return null;
        }
    }

    /**
     * Extracts the fbc tracking parameter from cookies or the URL.
     * @method getFbc
     * @memberof DataManager
     * @returns {string|null} The fbc value or null if not found.
     */
    getFbc() {  
        const fbcCookie = this.getCookie("_fbc");
        Logger.log("Retrieved fbc:", fbcCookie);
        if (fbcCookie) return fbcCookie;

        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get("fbclid");
        Logger.log("Retrieved fbclid from URL:", fbclid);

        return fbclid || null;
    }

    /**
     * Retrieves the Reddit click ID (rdt_cid) from the URL parameters.
     * @method getRdtCid
     * @memberof DataManager
     * @returns {string|null} The rdt_cid value or null if not found.
     */
    getRdtCid() {
        Logger.log("Retrieving Reddit click ID (rdt_cid) from URL parameters.");
        const params = new URLSearchParams(window.location.search);
        const rdtCid = params.get('rdt_cid');
        if ( rdtCid ) {
            Logger.log("Retrieved rdt_cid:", rdtCid);
        }
        return rdtCid || null;
    }

    /**
     * Retrieves a specific cookie by name.
     * @method getCookie
     * @memberof DataManager
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|null} The cookie value or null if not found.
     */
    getCookie( name ) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(";").shift();
        return null;
    }

    /**
     * Prepares a common payload for tracking events.
     * @async
     * @method prepareCommonPayload
     * @memberof DataManager
     * @param {string} eventName - The event name to track.
     * @param {Object} customData - Custom data to include in the payload.
     * @param {string} eventId - The unique ID for the event, generated in Analytics.
     * @returns {Object} The prepared event payload.
     */
    async prepareCommonPayload( eventName, customData, eventId ) {
        const rawUserData = this.getUserData();
        const consentData = JSON.parse(decodeURIComponent(this.getCookie("consentData") || "{}"));

        const userData = this.cleanFields({
            external_id: rawUserData.external_id || "anonymous",
            client_user_agent: navigator.userAgent,
            fbq: rawUserData.fbq,
            fbc: rawUserData.fbc,
            rdt_cid: rawUserData.rdt_cid,
            hashedEmail: rawUserData.hashedEmail,
        });

        const returnObject = {
            "event-type": eventName,
            eventId,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            source: getPageName(),
            userData,
            customData,
            noTrack: consentData.hasOptedOut,
        };

        return returnObject;
    }

    /**
     * Updates the stored hashed email in user data.
     * @async
     * @method updateUserEmail
     * @memberof DataManager
     * @param {string} email - The email to be hashed and stored.
     */
    async updateUserEmail( email) {
        const hashedEmail = await hashEmail(email);
        this.updateUserData({
            hashedEmail,
        });
    }

    /**
     * Cleans an object by removing keys with null or undefined values.
     * @param {Object} data - The raw user data object.
     * @returns {Object} The cleaned object without null or undefined fields.
     */
    cleanFields (data) {
        const cleaned = {};
        for (const key in data) {
            if (data[key] !== null && data[key] !== undefined) {
                cleaned[key] = data[key];
            }
        }
        return cleaned;
    }
}

export default DataManager;
